import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import { IdFieldSelect } from '../../components/formFields/IdFieldSelect';
import MultiSelectChips from '../../components/ui/MultiSelectChips';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  createCategoryAllocation,
  formatCategoryAllocationUserLabel,
  getCategoryAllocationById,
  listActiveMetaUsers,
  listStateAttorneys,
  listSupervisingOfficers,
  updateCategoryAllocation,
  type CategoryAllocationAllocatedUser,
  type CategoryAllocationUserOption,
} from '../../services/categoryAllocationSetup';

import { getFileCategories, getSubFileCategories, type LookupItem } from '../../services/criminalCaseRequests';
import { setFlashToast } from '../../utils/flashToast';
import { formatAuditDateTime } from '../../utils/dateFormat';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { subFileCategoryRequiredForCategory } from '../../utils/subFileCategoryRules';
import { useAuth } from '../../contexts/AuthContext';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import { checkCategoryAllocationExists } from '../../services/caseRequestCategoryAllocationCheck';

function toUserOptions(
  users: CategoryAllocationUserOption[],
  extraAllocated: CategoryAllocationAllocatedUser[] = [],
): { id: string; label: string }[] {
  const map = new Map<string, { id: string; label: string }>();
  for (const u of users) {
    map.set(u.userId, { id: u.userId, label: formatCategoryAllocationUserLabel(u) });
  }
  for (const u of extraAllocated) {
    if (!map.has(u.userId)) {
      map.set(u.userId, { id: u.userId, label: formatCategoryAllocationUserLabel(u) });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export default function UserCategoryFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { categoryAllocationId } = useParams();
  const isEdit = Boolean(categoryAllocationId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [subFileCategories, setSubFileCategories] = useState<LookupItem[]>([]);
  const [activeUsers, setActiveUsers] = useState<CategoryAllocationUserOption[]>([]);
  const [supervisors, setSupervisors] = useState<CategoryAllocationUserOption[]>([]);
  const [stateAttorneys, setStateAttorneys] = useState<CategoryAllocationUserOption[]>([]);

  const [fileCategoriesState, setFileCategoriesState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [subFileCategoriesState, setSubFileCategoriesState] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [lookupsState, setLookupsState] = useState<'idle' | 'loading' | 'ready'>('idle');

  const [fileCategoryId, setFileCategoryId] = useState('');
  const [subFileCategoryId, setSubFileCategoryId] = useState('');
  const [originalFileCategoryId, setOriginalFileCategoryId] = useState('');
  const [originalSubFileCategoryId, setOriginalSubFileCategoryId] = useState('');
  const [userIds, setUserIds] = useState<string[]>([]);
  const [supervisorId, setSupervisorId] = useState('');
  const [stateAttorneyId, setStateAttorneyId] = useState('');
  const [loadedAllocatedUsers, setLoadedAllocatedUsers] = useState<CategoryAllocationAllocatedUser[]>([]);
  const [loadedSupervisor, setLoadedSupervisor] = useState<CategoryAllocationUserOption | null>(null);
  const [loadedStateAttorney, setLoadedStateAttorney] = useState<CategoryAllocationUserOption | null>(null);

  const [createdAt, setCreatedAt] = useState('');
  const [createdByName, setCreatedByName] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedByName, setUpdatedByName] = useState('');

  // ── Duplicate-combination check state (Add mode only) ──────────────────────
  const [duplicateCheckState, setDuplicateCheckState] = useState<'idle' | 'checking' | 'done'>('idle');
  const [duplicateInfo, setDuplicateInfo] = useState<{
    categoryAllocationId: string;
    fileCategoryName: string;
    subFileCategoryName: string;
  } | null>(null);
  // ───────────────────────────────────────────────────────────────────────────

  const userOptions = useMemo(() => toUserOptions(activeUsers, loadedAllocatedUsers), [activeUsers, loadedAllocatedUsers]);
  const supervisorOptions = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    for (const s of supervisors) {
      map.set(s.userId, { id: s.userId, label: s.displayName.trim() || formatCategoryAllocationUserLabel(s) });
    }
    if (loadedSupervisor && !map.has(loadedSupervisor.userId)) {
      map.set(loadedSupervisor.userId, {
        id: loadedSupervisor.userId,
        label: loadedSupervisor.displayName.trim() || formatCategoryAllocationUserLabel(loadedSupervisor),
      });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [supervisors, loadedSupervisor]);

  const stateAttorneyOptions = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    for (const s of stateAttorneys) {
      map.set(s.userId, { id: s.userId, label: s.displayName.trim() || formatCategoryAllocationUserLabel(s) });
    }
    if (loadedStateAttorney && !map.has(loadedStateAttorney.userId)) {
      map.set(loadedStateAttorney.userId, {
        id: loadedStateAttorney.userId,
        label: loadedStateAttorney.displayName.trim() || formatCategoryAllocationUserLabel(loadedStateAttorney),
      });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [stateAttorneys, loadedStateAttorney]);

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setFileCategoriesState('loading');
    setLookupsState('loading');
    void Promise.all([getFileCategories(), listActiveMetaUsers(), listSupervisingOfficers(), listStateAttorneys()]).then(
      ([cats, users, sups, stateAttorneyRes]) => {
      if (!alive) return;
      setFileCategories(cats.success ? cats.data : []);
      setActiveUsers(users.success ? users.data : []);
      setSupervisors(sups.success ? sups.data : []);
      setStateAttorneys(stateAttorneyRes.success ? stateAttorneyRes.data : []);
      setFileCategoriesState('ready');
      setLookupsState('ready');
    });
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!fileCategoryId) {
      setSubFileCategories([]);
      setSubFileCategoriesState('ready');
      if (!isEdit) setSubFileCategoryId('');
      return;
    }
    let alive = true;
    setSubFileCategoriesState('loading');
    void getSubFileCategories(fileCategoryId).then((res) => {
      if (!alive) return;
      setSubFileCategories(res.success ? res.data : []);
      setSubFileCategoriesState('ready');
    });
    return () => {
      alive = false;
    };
  }, [fileCategoryId, isEdit]);

  useEffect(() => {
    if (!isEdit || !categoryAllocationId) return;
    let alive = true;
    setLoading(true);
    void getCategoryAllocationById({ categoryAllocationId }).then((res) => {
      if (!alive) return;
      if (res.success) {
        const d = res.data;
        setFileCategoryId(d.fileCategoryId);
        setSubFileCategoryId(d.subFileCategoryId);
        setOriginalFileCategoryId(d.fileCategoryId);
        setOriginalSubFileCategoryId(d.subFileCategoryId);
        setLoadedAllocatedUsers(d.allocatedUserDetails);
        setUserIds(d.allocatedUserDetails.map((u) => u.userId));
        setSupervisorId(d.supervisor?.userId ?? '');
        if (d.supervisor) {
          setLoadedSupervisor({
            userId: d.supervisor.userId,
            title: d.supervisor.title,
            displayName: d.supervisor.displayName,
            firstName: d.supervisor.firstName,
            lastName: d.supervisor.lastName,
            username: '',
            roles: '',
          });
        }
        setStateAttorneyId(d.stateAttorney?.userId ?? '');
        if (d.stateAttorney) {
          setLoadedStateAttorney({
            userId: d.stateAttorney.userId,
            title: d.stateAttorney.title,
            displayName: d.stateAttorney.displayName,
            firstName: d.stateAttorney.firstName,
            lastName: d.stateAttorney.lastName,
            username: '',
            roles: '',
          });
        }
        setCreatedAt(formatAuditDateTime(d.createdAt) || d.createdAt);
        setCreatedByName(d.createdByName);
        setUpdatedAt(formatAuditDateTime(d.updatedAt) || d.updatedAt);
        setUpdatedByName(d.updatedByName);
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, categoryAllocationId]);

  // ── CMS-CCA-12: Check for duplicate combination (Add mode only) ────────────
  // Fires when file category is selected (sub file category optional for RTI / Public Petitions).
  useEffect(() => {
    if (isEdit) return;

    const selectedFileCategory = fileCategories.find((x) => x.id === fileCategoryId);
    const subRequired = subFileCategoryRequiredForCategory(
      { id: fileCategoryId, name: selectedFileCategory?.name },
      subFileCategories,
      subFileCategoriesState === 'loading',
    );

    if (!fileCategoryId || (subRequired && !subFileCategoryId)) {
      setDuplicateInfo(null);
      setDuplicateCheckState('idle');
      return;
    }

    const controller = new AbortController();
    setDuplicateCheckState('checking');
    setDuplicateInfo(null);

    void checkCategoryAllocationExists({
      fileCategoryId,
      subFileCategoryId,
      fileCategoryName: selectedFileCategory?.name,
      signal: controller.signal,
    }).then((res) => {
      if (controller.signal.aborted) return;
      if (res.success && res.data.allocationExists) {
        setDuplicateInfo({
          categoryAllocationId: res.data.categoryAllocationId,
          fileCategoryName: res.data.fileCategoryName,
          subFileCategoryName: res.data.subFileCategoryName,
        });
      } else {
        setDuplicateInfo(null);
      }
      setDuplicateCheckState('done');
    });

    return () => controller.abort();
  }, [isEdit, fileCategoryId, subFileCategoryId, fileCategories, subFileCategories, subFileCategoriesState]);
  // ───────────────────────────────────────────────────────────────────────────

  const subFileCategoryRequired = useMemo(
    () =>
      subFileCategoryRequiredForCategory(
        { id: fileCategoryId, name: fileCategories.find((x) => x.id === fileCategoryId)?.name },
        subFileCategories,
        subFileCategoriesState === 'loading',
      ),
    [fileCategoryId, fileCategories, subFileCategories, subFileCategoriesState],
  );

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(
    () => [
      { key: 'fileCategoryId', label: t('admin.shared.fileCategory'), value: fileCategoryId, elementId: 'userCategory-fileCategory' },
      ...(subFileCategoryRequired
        ? [{ key: 'subFileCategoryId', label: t('admin.shared.subFileCategory'), value: subFileCategoryId, elementId: 'userCategory-subFileCategory' }]
        : []),
      { key: 'userIds', label: t('admin.userCategoryForm.userNames'), value: userIds, elementId: 'userCategory-users' },
      { key: 'supervisorId', label: t('admin.shared.supervisor'), value: supervisorId, elementId: 'userCategory-supervisor' },
      { key: 'stateAttorneyId', label: t('tasks.assign.stateAttorney'), value: stateAttorneyId, elementId: 'userCategory-stateAttorney' },
    ],
    [t, fileCategoryId, subFileCategoryId, supervisorId, stateAttorneyId, userIds, subFileCategoryRequired],
  );

  async function handleSave() {
    if (savingRef.current) return;

    // Hard-block if duplicate combination detected (Add mode)
    if (!isEdit && duplicateInfo) return;

    const errs = validateRequired(requiredSpecs);
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(requiredSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      if (isEdit && categoryAllocationId) {
        const categoriesChanged =
          fileCategoryId !== originalFileCategoryId || subFileCategoryId !== originalSubFileCategoryId;
        const res = categoriesChanged
          ? await createCategoryAllocation({
              fileCategoryId,
              subFileCategoryId,
              supervisorId,
              stateAttorneyId,
              userIds,
            })
          : await updateCategoryAllocation({
              categoryAllocationId,
              supervisorId,
              stateAttorneyId,
              userIds,
            });
        if (!res.success) {
          setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
          return;
        }
        setFlashToast({ message: res.data.message || t('admin.userCategoryForm.updateSuccess'), variant: 'success' });
      } else {
        const res = await createCategoryAllocation({
          fileCategoryId,
          subFileCategoryId,
          supervisorId,
          stateAttorneyId,
          userIds,
        });
        if (!res.success) {
          setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
          return;
        }
        setFlashToast({ message: res.data.message || t('admin.userCategoryForm.createSuccess'), variant: 'success' });
      }
      nav('/admin/user-categories');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  const lookupsLoading = lookupsState === 'loading' || fileCategoriesState === 'loading';
  const isDuplicateBlocked = !isEdit && !!duplicateInfo;
  const isCheckingDuplicate = !isEdit && duplicateCheckState === 'checking';

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        <Link to="/admin/intranet-users">{t('admin.intranetUsers.title')}</Link>
        <span className="bc-sep">/</span>
        <Link to="/admin/user-categories">{t('admin.userCategories.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('admin.userCategoryForm.updateTitle') : t('admin.userCategoryForm.addTitle')}
      </div>

      <div className="pg-title">{isEdit ? t('admin.userCategoryForm.updateTitle') : t('admin.userCategoryForm.addTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          {/* ── Duplicate combination warning banner ── */}
          {isDuplicateBlocked && duplicateInfo ? (
            <div
              className="si-err"
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}
              role="alert"
            >
         
              <span>
                {t('admin.userCategories.duplicateCombinationError', {
                  fileCategory: duplicateInfo.fileCategoryName,
                  subFileCategory: duplicateInfo.subFileCategoryName,
                })}{' '}
                <Link
                  to={`/admin/user-categories/${encodeURIComponent(duplicateInfo.categoryAllocationId)}/edit`}
                  style={{ fontWeight: 600, textDecoration: 'underline' }}
                >
                  {t('admin.userCategories.editExistingRecord')}
                </Link>
              </span>
            </div>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <IdFieldSelect
              id="userCategory-fileCategory"
              label={t('admin.shared.fileCategory')}
              required
              value={fileCategoryId}
              placeholder={fileCategoriesState === 'loading' ? t('common.loading') : t('admin.userCategories.selectFileCategory')}
              options={fileCategories.map((x) => ({ id: x.id, label: x.name }))}
              error={reqErrors.fileCategoryId}
              disabled={lookupsLoading || loading}
              onChange={(v) => {
                setFileCategoryId(v);
                setSubFileCategoryId('');
                setDuplicateInfo(null);
                setDuplicateCheckState('idle');
                clearReqError('fileCategoryId');
                clearReqError('subFileCategoryId');
              }}
            />
            <IdFieldSelect
              id="userCategory-subFileCategory"
              label={t('admin.shared.subFileCategory')}
              required={subFileCategoryRequired}
              value={subFileCategoryId}
              placeholder={
                !fileCategoryId
                  ? t('admin.userCategoryForm.selectFileCategoryFirst')
                  : subFileCategoriesState === 'loading'
                    ? t('common.loading')
                    : t('admin.userCategories.selectSubFileCategory')
              }
              options={subFileCategories.map((x) => ({ id: x.id, label: x.name }))}
              error={reqErrors.subFileCategoryId}
              disabled={!fileCategoryId || subFileCategoriesState === 'loading' || loading}
              onChange={(v) => {
                setSubFileCategoryId(v);
                clearReqError('subFileCategoryId');
              }}
            />
            <MultiSelectChips
              id="userCategory-users"
              label={t('admin.userCategoryForm.userNames')}
              required
              value={userIds}
              options={userOptions}
              placeholder={t('admin.userCategoryForm.searchSelectUsers')}
              searchPlaceholder={t('admin.userCategoryForm.searchSelectUsers')}
              error={reqErrors.userIds}
              disabled={lookupsLoading || loading}
              emptyMessage={lookupsLoading ? t('common.loading') : t('admin.userCategoryForm.noUsersFound')}
              onChange={(v) => {
                setUserIds(v);
                clearReqError('userIds');
              }}
            />
            <IdFieldSelect
              id="userCategory-supervisor"
              label={t('admin.shared.supervisor')}
              required
              value={supervisorId}
              placeholder={lookupsLoading ? t('common.loading') : t('admin.userCategories.selectSupervisor')}
              options={supervisorOptions}
              error={reqErrors.supervisorId}
              disabled={lookupsLoading || loading}
              onChange={(v) => {
                setSupervisorId(v);
                clearReqError('supervisorId');
              }}
            />
            <IdFieldSelect
              id="userCategory-stateAttorney"
              label={t('tasks.assign.stateAttorney')}
              required
              value={stateAttorneyId}
              placeholder={lookupsLoading ? t('common.loading') : t('admin.userCategories.selectStateAttorney')}
              options={stateAttorneyOptions}
              error={reqErrors.stateAttorneyId}
              disabled={lookupsLoading || loading}
              onChange={(v) => {
                setStateAttorneyId(v);
                clearReqError('stateAttorneyId');
              }}
            />
          </div>

          {isEdit ? (
            <SystemInformationSection title={t('profile.systemInformation')}>
              <SystemInfoBox label={t('profile.createdAt')} value={createdAt} />
              <SystemInfoBox label={t('profile.createdBy')} value={createdByName} />
              <SystemInfoBox label={t('profile.lastUpdatedAt')} value={updatedAt} />
              <SystemInfoBox label={t('profile.lastUpdatedBy')} value={updatedByName} />
            </SystemInformationSection>
          ) : null}
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading || isDuplicateBlocked || isCheckingDuplicate}
              title={isDuplicateBlocked ? t('admin.userCategories.duplicateCombinationBlockedTooltip') : undefined}
            >
              {isCheckingDuplicate
                ? t('common.checking')
                : saving
                  ? t('common.saving')
                  : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('admin.shared.cancelChangesMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/admin/user-categories');
        }}
      />
    </>
  );
}
//text